import { Blueprint } from '../../blueprint.class';
import { ToolResult } from '../../types';
import { MoveGroupToPanelResult, RearrangeRefusedError, moveApplianceGroupToPanelCfn } from '../rearrange';

export interface MoveApplianceToIslandArgs {
	applianceId: string;
	islandId: string;
	referenceApplianceId?: string;
	position?: 'left' | 'right';
	/** Let the receiving island grow when it has no room (default true; size-locked islands never grow) */
	autoSize?: boolean;
	/** Soft cap for a grown island, inches */
	maxWidth?: number | null;
}

/**
 * Moves an appliance column to another island's front panel, ids preserved,
 * companions handled by the rules engine (trim kit regenerated at the
 * destination, vents re-evaluated on the island it left). Same vocabulary as
 * move_appliance_group: land left/right of a reference appliance, or at an end
 * of the island.
 */
export const moveApplianceToIslandTool = (blueprint: Blueprint, { applianceId, islandId, referenceApplianceId, position, autoSize = true, maxWidth = null }: MoveApplianceToIslandArgs): ToolResult<MoveGroupToPanelResult> => {
	if (!applianceId) return { success: false, error: 'Missing applianceId. Use list_appliances to get valid item IDs.' };
	if (!islandId) return { success: false, error: 'Missing islandId. Use list_islands to get valid island IDs.' };
	if (position && position !== 'left' && position !== 'right') return { success: false, error: "Invalid position. Must be 'left' or 'right'." };

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	const ref = kitchen.getReferenceMap(applianceId);
	if (!ref?.panelItemGroup || !ref.panel || !ref.island) return { success: false, error: `Appliance '${applianceId}' not found. Use list_appliances to get valid item IDs.` };

	const island = kitchen.design.islands.children.find((i) => i.id === islandId);
	if (!island) return { success: false, error: `Island '${islandId}' not found. Use list_islands to get valid island IDs.` };
	const targetPanel = island.panels.getFrontPanel();
	if (!targetPanel) return { success: false, error: `Island '${islandId}' has no front panel.` };

	let x: number;
	if (referenceApplianceId) {
		const refRef = kitchen.getReferenceMap(referenceApplianceId);
		if (!refRef?.panelItemGroup || !refRef.panel) return { success: false, error: `Reference appliance '${referenceApplianceId}' not found.` };
		if (refRef.panel.id !== targetPanel.id) return { success: false, error: 'The reference appliance must be on the target island.' };
		if (refRef.panelItemGroup.id === ref.panelItemGroup.id) return { success: false, error: 'The appliance and the reference appliance are in the same column.' };
		x = refRef.panelItemGroup.center.x + (position === 'left' ? -1 : 1);
	} else {
		const halfWidth = targetPanel.size.width / 2;
		x = position === 'left' ? -halfWidth + 1 : halfWidth - 1;
	}

	try {
		const result = moveApplianceGroupToPanelCfn(kitchen, ref.panelItemGroup, targetPanel, x, { growable: autoSize && !island.sizeLocked, maxWidth });
		const resizedNote = result.resized ? ` Island resized ${result.resized.fromWidthIn}" -> ${result.resized.toWidthIn}".` : '';
		const where = referenceApplianceId ? `${position ?? 'right'} of '${referenceApplianceId}'` : `${position ?? 'right'} end`;
		return { success: true, message: `Moved '${applianceId}' to island '${islandId}' (${where}).${resizedNote}`, data: result };
	} catch (error) {
		if (error instanceof RearrangeRefusedError) return { success: false, errorCode: error.reason, error: error.message };
		throw error;
	}
};
